import { EventEmitter, WritableSignal } from "@angular/core";

export type ChartDestination = 'project' | 'user' | 'company';

export interface Chart {
  id: string;
  title?: string;
  projects?: string[];
  destination: ChartDestination;
  company?: string;   // synthesis widget: its company
  databaseSeries: boolean; // default: false
  category?: string;
  compareMode: boolean; // default: false
  visible: boolean; // default: true
  template: boolean; // default: false
  isInReport: boolean; // default: false
  type?: string;
  graphType?: string;
  graphOption?: string,
  index?: string;
  width?: number;
  height?: number;
  user: string; // User id
  dataset: {
    enable?: boolean;
    label?: string;
    data: any[];
  };
  design: Record<string, any>; // default: {}
  query: Record<string, any>; // default: {}
  content: Record<string, any>; // default: {}
  version: number; // default: 0
  refreshDate: string | number; // default: Date.now()
  createdDate: string | number; // default: Date.now()
}

export interface WidgetV2 {
  id?: string,
  title?: string,
  projects?: any[],
  type?: string,
  graphType?: string,
  graphOption?: string,
  chartType?: string,
  dashboard?: string,
  visible?: boolean,
  z?: string,
  dataset?: any,
  data?: any,
  query?: any,
  design?: any,
  category?: string,
  databaseSeries?: boolean,
  index?: string,
  width?: number,
  height?: number,
  theme?: string,
  content?: any,
  destination?: string,
  user?: string,
  refresh?: EventEmitter<string>,
  export?: EventEmitter<string>,
  createdDate?: string,
  template?: boolean,
  version?: number,
  showEdit?: boolean,
  datasetNames?: Record<string, string>,
  compareMode?: boolean,
  signalSingled?: WritableSignal<string>,
}

export interface PostChart {
  title?: string;
  projects?: string[];
  destination: ChartDestination;
  company?: string;   // synthesis widget: its company
  databaseSeries?: boolean;  // default: false
  category?: string;
  compareMode?: boolean;  // default: false
  visible?: boolean;  // default: true
  template?: boolean;  // default: false
  isInReport?: boolean;  // default: false
  type?: string;
  graphType?: string;
  graphOption?: string;
  index?: string;
  width?: number;
  height?: number;
  user: string;  // User id
  dataset: {
    enable?: boolean;
    label?: string;
    data: any[];
  };
  design?: Record<string, any>;  // default: {}
  query?: Record<string, any>;  // default: {}
  content?: Record<string, any>;  // default: {}
  version?: number;  // default: 0
  refreshDate?: string | number;  // default: Date.now()
  createdDate?: string | number;  // default: Date.now()
}

export interface PutChart {
  id: string;
  title?: string;
  projects?: string[];
  destination?: 'project' | 'user' | 'company';
  databaseSeries?: boolean;
  category?: string;
  compareMode?: boolean;
  visible?: boolean;
  template?: boolean;
  isInReport?: boolean;
  type?: string;
  graphType?: string;
  graphOption?: string,
  index?: string;
  width?: number;
  height?: number;
  user?: string;  // User id (might not be updated)
  dataset?: {
    enable?: boolean;
    label?: string;
    data?: any[];  // Might not be updated every time
  };
  design?: Record<string, any>;
  query?: Record<string, any>;
  content?: Record<string, any>;
  version?: number;
  refreshDate?: string | number;
  createdDate?: string | number;  // Typically should not be updated
}

// Synthesis widget types that compare the tenant projects by default (`query.byProject`); the others show a total.
export const BY_PROJECT_WIDGET_TYPES: readonly string[] = ['chart', 'table'];
// Synthesis widget types whose rows are the projects: always compared, no option.
export const PROJECT_ROWS_WIDGET_TYPES: readonly string[] = ['heatmap', 'projectMap'];

// Prediction (query.forecast): only over a date X axis binned by hour..year, not periodic.
export interface ChartForecast {
  enabled: boolean;
  horizonDays: number;                        // 1..730
  // auto: the API backtests its models on the latest bins and keeps the most accurate one.
  method: 'auto' | 'holt' | 'linear' | 'poisson';
  confidence: 0.8 | 0.9 | 0.95 | 0.99;
}

export const defaultForecast: ChartForecast = { enabled: true, horizonDays: 90, method: 'auto', confidence: 0.9 };
export const FORECAST_GRAPH_TYPES: readonly string[] =
  ['barStacked', 'barGrouped', 'columnStacked', 'columnGrouped', 'discreteLine', 'continiousLine', 'discreteArea', 'continiousArea', 'insight'];
export const FORECAST_BINNINGS: readonly string[] = ['hour', 'day', 'week', 'month', 'year'];

// query.compare.previous: the same query over the window of the same length just before (needs a date filter).
// meta.previous: its rows, or why there are none.
export interface PreviousMeta {
  rows?: any[];
  // The window compared against (ISO dates, `to` excluded).
  from?: string;
  to?: string;
  reason?: 'no_date_filter';
}

// How reliable a forecast proved: backtested on the latest bins (WAPE: share of the volume missed, MASE: error
// compared with "like the previous season"), or for Poisson the width of the rate interval and the dispersion.
export type ForecastQualityLevel = 'good' | 'fair' | 'poor';

export interface ForecastQuality {
  level: ForecastQualityLevel | null;
  wape?: number | null;
  mase?: number | null;
  events?: number;
  dispersion?: number;
}

export type ForecastModel = 'naive' | 'holt' | 'holtWinters' | 'linear' | 'poisson';
export type ForecastReason = 'not_enough_history' | 'not_enough_events' | 'poisson_needs_count';

export interface PoissonMeta {
  events: number;
  days: number;
  ratePerDay: number;
  rateRange: [number, number];
  meanDays: number | null;
  meanDaysRange: [number | null, number | null];
  medianDays: number | null;
  dueDate: string | null;
  trend: 'up' | 'down' | 'stable';
  p: Record<string, number>;
}

export interface ForecastSeriesMeta {
  s?: string;
  p?: string;
  method?: ForecastModel;
  quality?: ForecastQuality | null;
  trendPct?: number | null;
  // outliers: isolated peaks left out of the fit; capped: the horizon went beyond what the binning allows.
  outliers?: number;
  capped?: boolean;
  reason?: ForecastReason;
  poisson?: PoissonMeta;
}

// meta.forecast of an aggregate run with the option: one entry per series (s, p).
export interface ForecastMeta {
  method: string;
  horizonDays: number;
  confidence: number;
  series: ForecastSeriesMeta[];
}

// POST /charts/aggregate/tenant: one query over every project of a tenant.
// byProject (default true): each row also carries `p`, its project name; false: one merged total.
export interface TenantAggregateFailure {
  project: string;
  name: string;
  reason: string;
}

export interface TenantAggregate {
  rows: any[];        // same rows as /charts/aggregate
  meta?: Record<string, unknown>;
  sources: { ok: number; failed: TenantAggregateFailure[] };
}

// Catalog templates: their theme, and the only fields the API keeps (POST / PUT /charts/templates).
export type TemplateTheme = 'general' | 'prevention' | 'production' | 'safety' | 'environment';
export const TEMPLATE_THEMES: readonly TemplateTheme[] = ['prevention', 'production', 'safety', 'environment', 'general'];
export const TEMPLATE_DESCRIPTION_MAX: number = 300;
const TEMPLATE_FIELDS: readonly string[] = ['title', 'description', 'theme', 'type', 'graphType', 'category', 'design', 'query', 'content', 'width', 'height', 'databaseSeries', 'compareMode'];

export interface TemplateMeta {
  title: string;
  theme: TemplateTheme;
  description?: string;
}

export function templateBody(widget: WidgetV2, meta: TemplateMeta): Record<string, any> {
  const source: Record<string, any> = { ...widget, ...meta };
  return Object.fromEntries(TEMPLATE_FIELDS.filter(field => source[field] !== undefined).map(field => [field, JSON.parse(JSON.stringify(source[field]))]));
}
